import type { AmhsStreamRunRequest } from '../../types/amhsStream';
import {
  AMHS_STREAM_DISPATCH_POLICIES,
  normalizeStreamDispatchPolicy,
} from './amhsStreamDispatch';

type JsonRecord = Record<string, unknown>;

function isRecord(value: unknown): value is JsonRecord {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function requireRecord(value: unknown, path: string, errors: string[]): JsonRecord | null {
  if (isRecord(value)) return value;
  errors.push(`${path} must be an object.`);
  return null;
}

function requireArray(value: unknown, path: string, errors: string[]): unknown[] {
  if (Array.isArray(value)) return value;
  errors.push(`${path} must be an array.`);
  return [];
}

function requireBoolean(value: unknown, path: string, errors: string[]): void {
  if (typeof value !== 'boolean') errors.push(`${path} must be a boolean.`);
}

function requireString(value: unknown, path: string, errors: string[]): void {
  if (typeof value !== 'string' || value.trim().length === 0) {
    errors.push(`${path} must be a non-empty string.`);
  }
}

function requireNumber(
  value: unknown,
  path: string,
  errors: string[],
  options: { integer?: boolean; min?: number; exclusiveMin?: number } = {}
): void {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    errors.push(`${path} must be a finite number.`);
    return;
  }
  if (options.integer && !Number.isInteger(value)) {
    errors.push(`${path} must be an integer.`);
  }
  if (options.min != null && value < options.min) {
    errors.push(`${path} must be at least ${options.min}.`);
  }
  if (options.exclusiveMin != null && value <= options.exclusiveMin) {
    errors.push(`${path} must be greater than ${options.exclusiveMin}.`);
  }
}

function validateEntityArray(
  value: unknown,
  path: string,
  idKey: string,
  errors: string[],
  validate: (item: JsonRecord, itemPath: string) => void
): void {
  const items = requireArray(value, path, errors);
  if (items.length === 0) errors.push(`${path} must contain at least one item.`);
  const ids = new Set<string>();
  items.forEach((value, index) => {
    const itemPath = `${path}[${index}]`;
    const item = requireRecord(value, itemPath, errors);
    if (!item) return;
    requireString(item[idKey], `${itemPath}.${idKey}`, errors);
    if (typeof item[idKey] === 'string') {
      if (ids.has(item[idKey])) errors.push(`${itemPath}.${idKey} must be unique.`);
      ids.add(item[idKey]);
    }
    validate(item, itemPath);
  });
}

const LIVE_POSITIVE_FIELDS = [
  'simSpeed',
  'horizonSec',
  'tickWallSec',
  'publishEveryNTicks',
  'legLookaheadSec',
  'maxRetainedLegs',
  'maxRetainedTransportRequests',
  'maxRetainedFoups',
  'activationsPerTick',
  'planningBatchFoups',
  'minPendingFoups',
  'maxFoupsInSystem',
] as const;

const LIVE_NON_NEGATIVE_FIELDS = [
  'inventoryUpdateHoldWallSec',
  'pickupHoldWallSec',
  'dropHoldWallSec',
  'activationLeadWallSec',
  'collisionHoldRounds',
  'maxDeferLookaheads',
  'reservationPruneSec',
] as const;

/**
 * Validates user-edited JSON before it can replace the generated live request.
 * This deliberately validates the wire contract without writing to project state.
 */
export function validateAmhsStreamRequest(value: unknown): string[] {
  const errors: string[] = [];
  const request = requireRecord(value, 'config', errors);
  if (!request) return errors;

  const loadPorts = requireRecord(request.loadPorts, 'loadPorts', errors);
  validateEntityArray(
    loadPorts?.loadPorts,
    'loadPorts.loadPorts',
    'loadPortId',
    errors,
    (port, path) => {
      requireString(port.trackNodeId, `${path}.trackNodeId`, errors);
      requireString(port.portKind, `${path}.portKind`, errors);
      if (port.foupCapacity != null) {
        requireNumber(port.foupCapacity, `${path}.foupCapacity`, errors, { min: 0 });
      }
    }
  );

  const tools = requireRecord(request.tools, 'tools', errors);
  validateEntityArray(tools?.tools, 'tools.tools', 'toolId', errors, (tool, path) => {
    requireString(tool.name, `${path}.name`, errors);
    requireString(tool.toolType, `${path}.toolType`, errors);
    requireNumber(tool.processWaitSec, `${path}.processWaitSec`, errors, { min: 0 });
    if (tool.foupCapacity != null) {
      requireNumber(tool.foupCapacity, `${path}.foupCapacity`, errors, { min: 0 });
    }
  });

  const stockers = requireRecord(request.stockers, 'stockers', errors);
  validateEntityArray(
    stockers?.stockers,
    'stockers.stockers',
    'stockerId',
    errors,
    (stocker, path) => {
      requireString(stocker.name, `${path}.name`, errors);
      requireNumber(stocker.foupCapacity, `${path}.foupCapacity`, errors, {
        integer: true,
        min: 0,
      });
      requireNumber(stocker.rawFoupCapacity, `${path}.rawFoupCapacity`, errors, {
        integer: true,
        min: 0,
      });
      requireNumber(stocker.processedFoupCapacity, `${path}.processedFoupCapacity`, errors, {
        integer: true,
        min: 0,
      });
      requireNumber(stocker.processWaitSec, `${path}.processWaitSec`, errors, { min: 0 });
      if (
        typeof stocker.foupCapacity === 'number' &&
        typeof stocker.rawFoupCapacity === 'number' &&
        typeof stocker.processedFoupCapacity === 'number' &&
        stocker.rawFoupCapacity + stocker.processedFoupCapacity !== stocker.foupCapacity
      ) {
        errors.push(`${path}.rawFoupCapacity + processedFoupCapacity must equal foupCapacity.`);
      }
    }
  );

  const ohts = requireRecord(request.ohts, 'ohts', errors);
  validateEntityArray(ohts?.ohts, 'ohts.ohts', 'ohtId', errors, (oht, path) => {
    requireString(oht.name, `${path}.name`, errors);
    requireString(oht.homeNodeId, `${path}.homeNodeId`, errors);
    requireString(oht.currentNodeId, `${path}.currentNodeId`, errors);
    requireNumber(oht.cruiseSpeed, `${path}.cruiseSpeed`, errors, { exclusiveMin: 0 });
    requireNumber(oht.lengthM, `${path}.lengthM`, errors, { exclusiveMin: 0 });
    requireNumber(oht.minSeparationM, `${path}.minSeparationM`, errors, { min: 0 });
    if (oht.ohtMode === 'dedicated') {
      requireString(oht.ohtRole, `${path}.ohtRole`, errors);
      if (
        oht.ohtRole !== 'outerloop' &&
        (!Array.isArray(oht.dedicatedPath) || oht.dedicatedPath.length === 0)
      ) {
        errors.push(`${path}.dedicatedPath must contain at least one step for a dedicated OHT.`);
      }
    }
  });
  const hasOuterLoopOht =
    Array.isArray(ohts?.ohts) &&
    ohts.ohts.some(
      (oht) => isRecord(oht) && oht.ohtMode === 'dedicated' && oht.ohtRole === 'outerloop'
    );
  if (
    hasOuterLoopOht &&
    (!Array.isArray(ohts?.interbayDedicatedPath) || ohts.interbayDedicatedPath.length === 0)
  ) {
    errors.push(
      'ohts.interbayDedicatedPath must contain at least one step when outer-loop OHTs exist.'
    );
  }

  const trackNetwork = requireRecord(request.trackNetwork, 'trackNetwork', errors);
  if (trackNetwork) {
    requireString(trackNetwork.fabId, 'trackNetwork.fabId', errors);
    const network = requireRecord(trackNetwork.trackNetwork, 'trackNetwork.trackNetwork', errors);
    if (network) {
      requireArray(network.nodes, 'trackNetwork.trackNetwork.nodes', errors);
      requireArray(network.segments, 'trackNetwork.trackNetwork.segments', errors);
      const junctions = requireArray(
        network.junctions,
        'trackNetwork.trackNetwork.junctions',
        errors
      );
      junctions.forEach((value, index) => {
        const path = `trackNetwork.trackNetwork.junctions[${index}]`;
        const junction = requireRecord(value, path, errors);
        if (!junction) return;
        requireNumber(junction.waitSec, `${path}.waitSec`, errors, { min: 0 });
      });
    }
  }

  if (request.foupReleaseMode !== 'segment-free') {
    errors.push('foupReleaseMode must be "segment-free".');
  }

  const handling = requireRecord(request.handling, 'handling', errors);
  if (handling) {
    requireNumber(handling.dropToPickSec, 'handling.dropToPickSec', errors, {
      exclusiveMin: 0,
    });
  }

  const inventory = requireRecord(request.inventory, 'inventory', errors);
  if (inventory) {
    if (typeof inventory.enabled !== 'boolean') {
      errors.push('inventory.enabled must be a boolean.');
    }
    const columns = requireArray(inventory.stockerColumns, 'inventory.stockerColumns', errors);
    const kinds = columns
      .filter(isRecord)
      .map((column) => column.kind)
      .filter((kind): kind is string => typeof kind === 'string');
    if (columns.length !== 2 || !kinds.includes('raw') || !kinds.includes('processed')) {
      errors.push(
        'inventory.stockerColumns must contain exactly one raw and one processed column.'
      );
    }
  }

  const assignment = requireRecord(request.ohtAssignment, 'ohtAssignment', errors);
  if (assignment) {
    if (assignment.enabled !== true) {
      errors.push('ohtAssignment.enabled must be true.');
    }
    ['policies', 'compareBaseline'].forEach((field) => {
      if (assignment[field] != null) {
        errors.push(`ohtAssignment.${field} is not supported on the live stream path.`);
      }
    });
    const policy =
      normalizeStreamDispatchPolicy(assignment.dispatchPolicy) ??
      ('nearest' as const);
    if (
      assignment.dispatchPolicy != null &&
      !AMHS_STREAM_DISPATCH_POLICIES.includes(policy)
    ) {
      errors.push(
        `ohtAssignment.dispatchPolicy must be one of: ${AMHS_STREAM_DISPATCH_POLICIES.join(', ')}.`
      );
    }
    if (typeof assignment.preferPreferredOht !== 'boolean') {
      errors.push('ohtAssignment.preferPreferredOht must be a boolean.');
    }
    if (typeof assignment.allowIntrabayEmptyTravel !== 'boolean') {
      errors.push('ohtAssignment.allowIntrabayEmptyTravel must be a boolean.');
    }
    const selection = requireRecord(
      assignment.vehicleSelection,
      'ohtAssignment.vehicleSelection',
      errors
    );
    if (selection) requireArray(selection.order, 'ohtAssignment.vehicleSelection.order', errors);

    if (policy === 'ortools') {
      if (assignment.useOrtools !== true) {
        errors.push('ohtAssignment.useOrtools must be true when dispatchPolicy is "ortools".');
      }
      requireNumber(
        assignment.ortoolsTimeLimitS,
        'ohtAssignment.ortoolsTimeLimitS',
        errors,
        { exclusiveMin: 0 }
      );
      if (typeof assignment.intelligentSharedOht !== 'boolean') {
        errors.push('ohtAssignment.intelligentSharedOht must be a boolean.');
      }
      if (assignment.sharedOhtMinGainSec != null) {
        requireNumber(
          assignment.sharedOhtMinGainSec,
          'ohtAssignment.sharedOhtMinGainSec',
          errors,
          { min: 0 }
        );
      }
      if (typeof assignment.intelligentDedicatedAssist !== 'boolean') {
        errors.push('ohtAssignment.intelligentDedicatedAssist must be a boolean.');
      }
      if (typeof assignment.dedicatedJobsFirst !== 'boolean') {
        errors.push('ohtAssignment.dedicatedJobsFirst must be a boolean.');
      }
    } else if (assignment.useOrtools === true) {
      errors.push(
        `ohtAssignment.useOrtools is only allowed when dispatchPolicy is "ortools".`
      );
    }
  }

  if (request.includeCollisionAvoidance !== true) {
    errors.push('includeCollisionAvoidance must be true.');
  }

  requireBoolean(request.persist, 'persist', errors);
  requireBoolean(request.includeSimulation, 'includeSimulation', errors);
  requireBoolean(request.includeKinetic, 'includeKinetic', errors);
  requireBoolean(request.includeTransportFlow, 'includeTransportFlow', errors);
  requireBoolean(request.includeTransportRequests, 'includeTransportRequests', errors);
  requireBoolean(request.includeTransportationGraph, 'includeTransportationGraph', errors);
  requireBoolean(request.includeReports, 'includeReports', errors);
  requireBoolean(request.includeTrOptimization, 'includeTrOptimization', errors);
  requireBoolean(request.includeRouteOptimization, 'includeRouteOptimization', errors);
  requireBoolean(request.includeOhtAssignment, 'includeOhtAssignment', errors);
  requireNumber(request.toolProcessWaitSec, 'toolProcessWaitSec', errors, { min: 0 });
  const routeOptimization = requireRecord(request.routeOptimization, 'routeOptimization', errors);
  if (routeOptimization) {
    requireBoolean(routeOptimization.enabled, 'routeOptimization.enabled', errors);
    requireNumber(routeOptimization.maxCandidates, 'routeOptimization.maxCandidates', errors, {
      integer: true,
      min: 1,
    });
    requireString(routeOptimization.objective, 'routeOptimization.objective', errors);
    requireBoolean(routeOptimization.compareBaseline, 'routeOptimization.compareBaseline', errors);
  }
  const collisionAvoidance = requireRecord(
    request.collisionAvoidance,
    'collisionAvoidance',
    errors
  );
  if (collisionAvoidance) {
    if (collisionAvoidance.enabled !== true) {
      errors.push('collisionAvoidance.enabled must be true.');
    }
    requireString(collisionAvoidance.mode, 'collisionAvoidance.mode', errors);
    requireString(collisionAvoidance.onConflict, 'collisionAvoidance.onConflict', errors);
    requireString(collisionAvoidance.trackOccupancy, 'collisionAvoidance.trackOccupancy', errors);
    requireNumber(collisionAvoidance.headwaySec, 'collisionAvoidance.headwaySec', errors, {
      min: 0,
    });
    requireNumber(
      collisionAvoidance.defaultNodeCapacity,
      'collisionAvoidance.defaultNodeCapacity',
      errors,
      { integer: true, exclusiveMin: 0 }
    );
    requireNumber(
      collisionAvoidance.nodeClearanceSec,
      'collisionAvoidance.nodeClearanceSec',
      errors,
      { min: 0 }
    );
    requireNumber(collisionAvoidance.minNodeHoldSec, 'collisionAvoidance.minNodeHoldSec', errors, {
      min: 0,
    });
    requireNumber(
      collisionAvoidance.jointReleaseSlackSec,
      'collisionAvoidance.jointReleaseSlackSec',
      errors,
      { min: 0 }
    );
    requireString(collisionAvoidance.fallback, 'collisionAvoidance.fallback', errors);
    [
      'allowOppositeTraffic',
      'enforceNodeCapacity',
      'enforceMinSeparation',
      'jointForce',
      'absorbWaitIntoPreviousSegment',
      'returnHomeAfterLastJob',
    ].forEach((field) => {
      if (typeof collisionAvoidance[field] !== 'boolean') {
        errors.push(`collisionAvoidance.${field} must be a boolean.`);
      }
    });
  }

  const mes = requireRecord(request.mes, 'mes', errors);
  if (mes) {
    requireBoolean(mes.enabled, 'mes.enabled', errors);
    requireNumber(mes.releaseGapSec, 'mes.releaseGapSec', errors, { min: 0 });
    requireNumber(mes.maxFoupsInSystem, 'mes.maxFoupsInSystem', errors, {
      integer: true,
      exclusiveMin: 0,
    });
    if (mes.maxFoups != null) {
      requireNumber(mes.maxFoups, 'mes.maxFoups', errors, { integer: true, exclusiveMin: 0 });
    }
  }

  const liveStream = requireRecord(request.liveStream, 'liveStream', errors);
  if (liveStream) {
    if (typeof liveStream.continuous !== 'boolean') {
      errors.push('liveStream.continuous must be a boolean.');
    }
    LIVE_POSITIVE_FIELDS.forEach((field) =>
      requireNumber(liveStream[field], `liveStream.${field}`, errors, { exclusiveMin: 0 })
    );
    LIVE_NON_NEGATIVE_FIELDS.forEach((field) =>
      requireNumber(liveStream[field], `liveStream.${field}`, errors, { min: 0 })
    );
  }

  return errors;
}

export function parseAmhsStreamRequestJson(json: string): {
  request: AmhsStreamRunRequest | null;
  errors: string[];
} {
  let value: unknown;
  try {
    value = JSON.parse(json);
  } catch (error) {
    return {
      request: null,
      errors: [error instanceof Error ? error.message : 'The JSON could not be parsed.'],
    };
  }
  const errors = validateAmhsStreamRequest(value);
  return {
    request: errors.length === 0 ? (value as AmhsStreamRunRequest) : null,
    errors,
  };
}
